import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IAlert } from '@types';
import { Button } from '@material-ui/core';
import { Box, Loading } from '@atoms';
import { GridFields, IGridFields } from '@molecules';
import styles from './alertDetails.module.scss';

interface IAlertDetails {
  alert: IAlert
  loading: boolean
  // eslint-disable-next-line @typescript-eslint/ban-types
  report: Function
}

/**
 * A template for the alert log page
 * Displays all the passed in alerts in a table
 */
export const AlertDetailsTemplate = ({
  alert,
  loading,
  report,
}: IAlertDetails) => {
  const navigate = useNavigate();
  const { createdAt, data, message, reportedFalse } = alert;
  const date = new Date(createdAt);

  /**
   * Returns to the alert log page
   */
  const returnToLog = () => {
    navigate('/alert-log');
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : '0' + month;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;

    return year + '-' + month + '-' + day;
  };

  const formattedDate = formatDate(date);
  const formattedTime = date.toLocaleTimeString();

  const overviewDatapoints: IGridFields['records'] = [
    {
      label: 'Message',
      value: message,
    },
    {
      label: 'Date Time',
      value: `${formattedDate} ${formattedTime}`,
    },
  ];

  if (reportedFalse) {
    overviewDatapoints.push({
      label: 'Reported as Invalid',
      warning: true,
    });
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      fullWidth
      fill
      column
      px={2}
      flexGrow={1}
      bgcolor="background.dark"
      color="text.primary"
      className={styles.alertDetails}
    >
      <Button
        className={styles.returnButton}
        color="primary"
        onClick={returnToLog}
        variant="contained"
      >
        Return to Log
      </Button>

      <h2>Overview</h2>

      <GridFields records={overviewDatapoints} />

      {!reportedFalse ? (
        <Button
          color="secondary"
          variant="contained"
          onClick={() => report(true)}
        >
          Report as Invalid
        </Button>
      ) : (
        <Button
          color="primary"
          variant="contained"
          onClick={() => report(false)}
        >
          Report as Valid
        </Button>
      )}

      <hr />

      <h2>Details</h2>

      {data?.map((vitalReading, index) => {
        const { bloodPressure, co2, frameUrl, heartRate, oxygen, time } =
          vitalReading;

        // Every field here is genuinely optional. A captured reading holds
        // whatever the camera could read at that instant, so a monitor showing
        // only heart rate and SpO2 stores null for the rest — and a room that
        // has only just come online stores a reading with nothing but a
        // timestamp. Destructuring null and calling .toString() on it threw
        // during render, and since the ErrorBoundary wraps the router that
        // blanked the ENTIRE dashboard: opening the audit trail for a routine
        // alert took the monitoring UI down.
        const [bpa, bpb] = bloodPressure || [];
        const missing = (value: unknown) => value === null || value === undefined;
        const show = (value: unknown) => (missing(value) ? '—' : String(value));

        const dataPoints: IGridFields['records'] = [
          {
            label: 'Heart Rate',
            value: show(heartRate),
          },
          {
            label: 'Oxygen',
            value: show(oxygen),
          },
          {
            label: 'Blood Pressure',
            value: missing(bpa) && missing(bpb) ? '—' : `${show(bpa)} / ${show(bpb)}`,
          },
          {
            label: 'Co2',
            value: show(co2),
          },
          {
            label: 'Recorded At',
            value: new Date(time).toLocaleTimeString(),
          },
        ];

        return (
          <div className={styles.vitalReading} key={index}>
            <img src={frameUrl} alt="Vital Frame" />

            <GridFields records={dataPoints} />
          </div>
        );
      })}
    </Box>
  );
};

export default AlertDetailsTemplate;
